// src/pages/AdmissionFormPage/AdmissionFormPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Navbar from '../../components/common/Navbar/Navbar';
import Footer from '../../components/common/Footer/Footer';
import { trackLead, trackCallClick, trackWhatsAppClick } from '../../utils/tracking';
import './AdmissionFormPage.css';

const AdmissionFormPage = () => {
    const [formData, setFormData] = useState({
        parentName: '',
        childName: '',
        childAge: '',
        phone: '',
        email: '',
        program: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (!formData.parentName || !formData.childName || !formData.childAge || !formData.phone || !formData.email || !formData.program) {
                throw new Error('Please fill all required fields');
            }

            const phoneDigits = formData.phone.replace(/\D/g, '');
            const cleanedPhone = phoneDigits.length === 12 && phoneDigits.startsWith('91') ? phoneDigits.slice(2) : phoneDigits;
            const phoneRegex = /^[6-9]\d{9}$/;
            if (!phoneRegex.test(cleanedPhone)) {
                throw new Error('Please enter a valid 10-digit phone number');
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                throw new Error('Please enter a valid email address');
            }

            // Save to Firestore
            await addDoc(collection(db, 'admission_enquiries'), {
                parentName: formData.parentName,
                childName: formData.childName,
                childAge: formData.childAge,
                phone: cleanedPhone,
                email: formData.email,
                program: formData.program,
                message: formData.message,
                source: 'website',
                createdAt: serverTimestamp(),
                submittedAt: new Date().toISOString()
            });

            // Email is sent automatically via Firebase Cloud Function + AWS SES
            // when the Firestore document is created above

            // Report conversion to Google Ads, GA4 and Meta
            trackLead({ program: formData.program, child_age: formData.childAge });

            setSuccess(true);
        } catch (err) {
            console.error('Submission Error:', err);
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="admission-page">
                <Navbar />
                <div className="admission-wrapper">
                    <motion.div
                        className="admission-success"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', duration: 0.6 }}
                    >
                        <div className="success-check">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <h2>Thank You!</h2>
                        <p>Your admission enquiry has been submitted successfully. Our team will get in touch with you shortly.</p>
                        <div className="success-info">
                            <div className="success-info-row">
                                <span>Child's Name</span>
                                <strong>{formData.childName}</strong>
                            </div>
                            <div className="success-info-row">
                                <span>Program</span>
                                <strong>{formData.program}</strong>
                            </div>
                            <div className="success-info-row">
                                <span>Contact</span>
                                <strong>{formData.phone}</strong>
                            </div>
                        </div>
                        <div className="success-actions">
                            <a href="tel:+919019829264" className="admission-btn admission-btn--outline" onClick={() => trackCallClick({ location: 'admission_success' })}>
                                Call Us
                            </a>
                            <a href="https://wa.me/919019829264" target="_blank" rel="noopener noreferrer" className="admission-btn admission-btn--primary" onClick={() => trackWhatsAppClick({ location: 'admission_success' })}>
                                WhatsApp Us
                            </a>
                        </div>
                        <button className="back-home-link" onClick={() => window.location.href = '/'}>
                            Back to Home
                        </button>
                    </motion.div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="admission-page">
            <Navbar />
            <div className="admission-wrapper">
                <div className="admission-container">
                    <motion.div
                        className="admission-header"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="admission-label">Admissions Open · 2026-27</span>
                        <h1>Begin Your Child's Journey</h1>
                        <p>Chat with us on WhatsApp for an instant reply, or fill the form below and our admissions team will reach out within 24 hours.</p>
                        <div className="admission-header-cta">
                            <a
                                href="https://wa.me/919019829264?text=Hi!%20I'm%20interested%20in%20admissions%20at%20Little%20Elly%20Kudlu."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admission-btn admission-btn--whatsapp"
                                onClick={() => trackWhatsAppClick({ location: 'admission_header' })}
                            >
                                💬 Chat on WhatsApp
                            </a>
                            <a
                                href="tel:+919019829264"
                                className="admission-btn admission-btn--outline"
                                onClick={() => trackCallClick({ location: 'admission_header' })}
                            >
                                📞 Call Us
                            </a>
                        </div>
                    </motion.div>

                    <motion.form
                        onSubmit={handleSubmit}
                        className="admission-form"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                    >
                        <div className="form-row">
                            <div className="form-field">
                                <label htmlFor="parentName">Parent / Guardian Name *</label>
                                <input
                                    type="text"
                                    id="parentName"
                                    name="parentName"
                                    value={formData.parentName}
                                    onChange={handleChange}
                                    placeholder="Full name"
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="phone">Phone Number *</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="10-digit mobile number"
                                    maxLength="10"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-field">
                                <label htmlFor="childName">Child's Name *</label>
                                <input
                                    type="text"
                                    id="childName"
                                    name="childName"
                                    value={formData.childName}
                                    onChange={handleChange}
                                    placeholder="Child's full name"
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="childAge">Child's Age *</label>
                                <select
                                    id="childAge"
                                    name="childAge"
                                    value={formData.childAge}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select age</option>
                                    <option value="1.5-2 years">1.5 - 2 years</option>
                                    <option value="2-3 years">2 - 3 years</option>
                                    <option value="3-4 years">3 - 4 years</option>
                                    <option value="4-5 years">4 - 5 years</option>
                                    <option value="5-6 years">5 - 6 years</option>
                                    <option value="6+ years">6+ years</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-field">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="program">Program Interested In *</label>
                                <select
                                    id="program"
                                    name="program"
                                    value={formData.program}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select program</option>
                                    <option value="Play Group">Play Group (1.5 - 2.5 yrs)</option>
                                    <option value="Nursery">Nursery (2.5 - 3.5 yrs)</option>
                                    <option value="LKG">LKG (3.5 - 4.5 yrs)</option>
                                    <option value="UKG">UKG (4.5 - 5.5 yrs)</option>
                                    <option value="Day Care">Day Care</option>
                                    <option value="Summer Camp">Summer Camp</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-field form-field--full">
                            <label htmlFor="message">Additional Message (Optional)</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Any specific questions or requirements..."
                                rows="4"
                            />
                        </div>

                        {error && (
                            <motion.div
                                className="admission-error"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}

                        <motion.button
                            type="submit"
                            className="admission-submit"
                            disabled={loading}
                            whileHover={{ scale: loading ? 1 : 1.02 }}
                            whileTap={{ scale: loading ? 1 : 0.98 }}
                        >
                            {loading ? (
                                <>
                                    <span className="admission-spinner"></span>
                                    Submitting...
                                </>
                            ) : (
                                'Submit Enquiry'
                            )}
                        </motion.button>

                        <p className="admission-note">
                            By submitting this form, you agree to be contacted by our admissions team.
                        </p>
                    </motion.form>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default AdmissionFormPage;
